import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { MultiSelect } from 'primereact/multiselect';
import { Dropdown } from 'primereact/dropdown';
import { InputNumber } from 'primereact/inputnumber';
import { Checkbox } from 'primereact/checkbox';
import { Button } from 'primereact/button';
import { Divider } from 'primereact/divider';
import countriesData from './countries.json'; // JSON file with all countries

const FiltersContainer = styled.div`
  .filters-header {
    display: flex;
    justify-content: flex-start;
    gap: 1rem;
    margin-bottom: 1rem;
  }

  .filters-grid {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
    gap: 1rem;
  }
  
  .filter-group {
    display: flex;
    flex-direction: column;
    height: 100%;
    
    label {
      font-weight: 600;
      margin-bottom: 0.5rem;
      color: var(--text-main);
      min-height: 2.4rem;
      display: flex;
      align-items: center;
      overflow-wrap: break-word;
      word-break: break-word;
    }

    > div:not(label) {
      flex: 1;
    }
  }

  .filters-grid > .filter-group {
    align-items: stretch;
  }
`;

const experienceLevels = [
  { label: 'Entry Level (0-2 years)', value: 'entry' },
  { label: 'Mid Level (2-5 years)', value: 'mid' },
  { label: 'Senior Level (5-10 years)', value: 'senior' },
  { label: 'Expert Level (10+ years)', value: 'expert' }
];

// Mock skills data - replace with API if available
const mockSkills = [
  { label: 'JavaScript', value: 'JavaScript' },
  { label: 'Python', value: 'Python' },
  { label: 'React', value: 'React' },
  { label: 'Node.js', value: 'Node.js' },
  { label: 'Django', value: 'Django' },
  { label: 'Flask', value: 'Flask' },
  { label: 'Vue.js', value: 'Vue.js' },
  { label: 'Angular', value: 'Angular' },
  { label: 'TypeScript', value: 'TypeScript' },
  { label: 'Java', value: 'Java' }
];

export default function SearchFilters({ filters, onFilterChange, onSearch, isLoading = false }) {
  const [availableSkills, setAvailableSkills] = useState([]);
  const [countries, setCountries] = useState([]);

  useEffect(() => {
    setAvailableSkills(mockSkills);
    setCountries(countriesData.map(c => ({ label: c.name, value: c.name })));
  }, []);

  const handleFilterChange = (key, value) => {
    onFilterChange(key, value);
  };

  const clearFilters = () => {
    onFilterChange('skills', []);
    onFilterChange('country', '');
    onFilterChange('experienceLevel', '');
    onFilterChange('minExperience', '');
    onFilterChange('recentlyActive', false);
  };

  const hasActiveFilters = () => {
    return filters.skills.length > 0 || 
           filters.country || 
           filters.experienceLevel || 
           filters.minExperience || 
           filters.recentlyActive;
  };

  return (
    <FiltersContainer>

      <div className="filters-header">
        {hasActiveFilters() && (
          <Button
            label="Clear Filters"
            icon="pi pi-times"
            className="p-button-outlined p-button-secondary"
            onClick={clearFilters}
          />
        )}
        <Button
          label="Search"
          icon="pi pi-search"
          className="p-button-primary"
          onClick={onSearch}
          disabled={isLoading}
          loading={isLoading}
        />
      </div>

      <Divider />

      <div className="filters-grid">
        <div className="filter-group">
          <label htmlFor="skills">Skills</label>
          <MultiSelect
            id="skills"
            value={filters.skills}
            onChange={(e) => handleFilterChange('skills', e.value)}
            options={availableSkills}
            placeholder="Select skills..."
            maxSelectedLabels={3}
            className="w-full"
          />
        </div>

        <div className="filter-group">
          <label htmlFor="country">Country</label>
          <Dropdown
            id="country"
            value={filters.country}
            onChange={(e) => handleFilterChange('country', e.value)}
            options={countries}
            placeholder="Select country..."
            className="w-full"
            showClear
          />
        </div>

        <div className="filter-group">
          <label htmlFor="experienceLevel">Experience Level</label>
          <Dropdown
            id="experienceLevel"
            value={filters.experienceLevel}
            onChange={(e) => handleFilterChange('experienceLevel', e.value)}
            options={experienceLevels}
            placeholder="Select level..."
            className="w-full"
            showClear
          />
        </div>

        <div className="filter-group">
          <label htmlFor="minExperience">Minimum Experience (years)</label>
          <InputNumber
            id="minExperience"
            value={filters.minExperience}
            onValueChange={(e) => handleFilterChange('minExperience', e.value)}
            placeholder="0"
            min={0}
            max={50}
            className="w-full"
          />
        </div>

        <div className="filter-group">
          <label htmlFor="recentlyActive">Recently Active</label>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
            <Checkbox
              id="recentlyActive"
              checked={filters.recentlyActive}
              onChange={(e) => handleFilterChange('recentlyActive', e.checked)}
            />
            <span style={{ fontSize: '0.9rem', color: 'var(--text-secondary)' }}>
              Show profiles updated in last 30 days
            </span>
          </div>
        </div>
      </div>
    </FiltersContainer>
  );
}

